import { AgencyDto, AgencyResponseDto } from '@/app/common/general-types/agency.dto';
import { FiltersGenericDto } from '@/app/services/user/user.service';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AgencyService } from '@/app/services/agency/agency.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { EmptyStateComponent } from '@/app/atoms/empty-state/empty-state.component';
import { formatDistanceToNow } from 'date-fns';
import { Profile } from './molecules/profile/profile';
import { Team } from './molecules/team/team';
import { Billing } from './molecules/billing/billing';
import { Brands } from './molecules/brands/brands';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  merge,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

type AgencyInsightsTab = 'profile' | 'billing' | 'team' | 'brands';

@Component({
  selector: 'ins-agency',
  imports: [Profile, Team, Billing, Brands, EmptyStateComponent],
  templateUrl: './agency.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Agency implements OnInit {
  private static readonly PAGE_SIZE = 25;
  private static readonly SCROLL_LOAD_THRESHOLD_PX = 80;

  pageNumber = signal(0);

  agencies = signal<AgencyDto[] | undefined>(undefined);
  agenciesCount = signal(0);
  agenciesLoading = signal(false);
  agenciesLoadingMore = signal(false);
  searchQuery = signal('');

  selectedAgency = signal<AgencyDto | undefined>(undefined);
  selectedTab = signal<AgencyInsightsTab>('profile');

  private searchTrigger = new Subject<string>();
  private searchSessionId = signal(0);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private agencyService = inject(AgencyService);
  private toastNotificationService = inject(ToastNotificationService);

  timeSinceCreated(createdDate: Date | string | undefined): string {
    if (createdDate == null) {
      return '—';
    }
    const d = typeof createdDate === 'string' ? new Date(createdDate) : createdDate;
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    return formatDistanceToNow(d, { addSuffix: true });
  }

  agencyInitial(agency: AgencyDto): string {
    const name = agency.name?.trim();
    return name ? name.charAt(0).toUpperCase() : 'A';
  }

  ngOnInit(): void {
    merge(of(''), this.searchTrigger.pipe(debounceTime(300), distinctUntilChanged()))
      .pipe(
        tap(() => {
          this.pageNumber.set(0);
          this.agenciesLoadingMore.set(false);
          this.searchSessionId.update((n) => n + 1);
        }),
        switchMap((term) => {
          this.agenciesLoading.set(true);
          return this.agencyService.getInsightsAgencies(this.buildFilters(0, term)).pipe(
            tap({
              next: () => this.agenciesLoading.set(false),
              error: () => this.agenciesLoading.set(false),
            }),
            catchError((err) => {
              console.error('Error fetching agencies:', err);
              this.toastNotificationService.alert({
                title: 'Error fetching agencies',
                message: 'Failed to retrieve agencies.',
                type: 'error',
                icon: 'error',
              });
              return of({ count: 0, data: [] } satisfies AgencyResponseDto);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.agencies.set(response.data);
        this.agenciesCount.set(response.count);
        this.syncSelectedAgencyFromRoute(response.data);
      });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const agencyId = params.get('agencyId');
      if (!agencyId) {
        this.selectedAgency.set(undefined);
        return;
      }
      const fromList = (this.agencies() ?? []).find((agency) => agency.id === agencyId);
      if (fromList) {
        this.selectedAgency.set(fromList);
      }
      this.loadAgencyById(agencyId);
    });
  }

  private loadAgencyById(agencyId: string): void {
    this.agencyService
      .getAgencyById(agencyId)
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((agency) => {
        if (!agency) {
          return;
        }
        const fromListNow = (this.agencies() ?? []).find((item) => item.id === agencyId);
        this.selectedAgency.set({ ...fromListNow, ...agency });
      });
  }

  private syncSelectedAgencyFromRoute(agencies: AgencyDto[]): void {
    const agencyId = this.route.snapshot.paramMap.get('agencyId');
    if (!agencyId) {
      return;
    }
    const match = agencies.find((agency) => agency.id === agencyId);
    if (match && !this.selectedAgency()) {
      this.selectedAgency.set(match);
    }
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchTrigger.next(value.trim());
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollHeight - scrollTop - clientHeight > Agency.SCROLL_LOAD_THRESHOLD_PX) {
      return;
    }
    this.loadMoreIfNeeded();
  }

  hasMore(): boolean {
    const list = this.agencies();
    if (list === undefined) {
      return false;
    }
    return list.length < this.agenciesCount();
  }

  private loadMoreIfNeeded(): void {
    if (this.agenciesLoading() || this.agenciesLoadingMore()) {
      return;
    }
    if (this.agencies() === undefined || !this.hasMore()) {
      return;
    }

    const nextPage = this.pageNumber() + 1;
    const term = this.searchQuery().trim();
    const session = this.searchSessionId();
    this.agenciesLoadingMore.set(true);

    this.agencyService
      .getInsightsAgencies(this.buildFilters(nextPage, term))
      .pipe(
        catchError((err) => {
          console.error('Error fetching more agencies:', err);
          this.toastNotificationService.alert({
            title: 'Error fetching agencies',
            message: 'Failed to load more agencies.',
            type: 'error',
            icon: 'error',
          });
          return of({ count: 0, data: [] } satisfies AgencyResponseDto);
        }),
        finalize(() => this.agenciesLoadingMore.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (session !== this.searchSessionId()) {
          return;
        }
        if (response.data.length === 0) {
          this.agenciesCount.set((this.agencies() ?? []).length);
          return;
        }
        this.agencies.update((prev) => [...(prev ?? []), ...response.data]);
        this.pageNumber.set(nextPage);
      });
  }

  private buildFilters(page: number, searchTerm: string): FiltersGenericDto {
    const term = searchTerm.trim();
    return {
      take: Agency.PAGE_SIZE,
      skip: page * Agency.PAGE_SIZE,
      sortField: 'createdDate',
      sortOrder: 'DESC',
      searchTerm: term || undefined,
    };
  }

  isSelectedAgency(agency: AgencyDto): boolean {
    return this.selectedAgency()?.id === agency.id;
  }

  onAgencySelect(agency: AgencyDto): void {
    this.selectedTab.set('profile');
    void this.router.navigate(['/insights/agency', agency.id]);
  }

  onAgencyProfileUpdated(agency: AgencyDto): void {
    this.selectedAgency.set(agency);
    this.agencies.update((prev) =>
      prev?.map((item) => (item.id === agency.id ? { ...item, ...agency } : item)),
    );
  }

  setSelectedTab(tab: AgencyInsightsTab): void {
    this.selectedTab.set(tab);
  }

  getSelectedTabClass(tab: AgencyInsightsTab): string {
    if (this.selectedTab() === tab) {
      return 'border-primary text-accent';
    }
    return 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700';
  }

  getSelectedTabIconClass(tab: AgencyInsightsTab): string {
    if (this.selectedTab() === tab) {
      return 'text-accent';
    }
    return 'text-gray-400 group-hover:text-gray-500';
  }
}
